import React from 'react';
import { Controller } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import classnames from 'classnames';
import PropTypes from 'prop-types';

export default function ControlledPhoneInput({
  control,
  dataTestid,
  defaultValue,
  disabled = false,
  errors,
  label,
  name,
  required,
  rules,
  className,
  onCountryChange,
  ...props
}) {
  const [lastChosenCountry, setLastChosenCountry] = React.useState('us');
  const [hasFocus, setHasFocus] = React.useState(false);
  const handleFocus = () => setHasFocus(true);
  const handleBlur = () => setHasFocus(false);
  const invalid = !!errors?.[name];

  React.useEffect(() => {
    onCountryChange(lastChosenCountry);
  }, [lastChosenCountry]);

  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      {...(rules && { rules })}
      render={({ field: { ref, onChange, value } }) => {
        return (
          <PhoneInput
            containerClass={classnames(
              /**
               * 'react-tel-input' is the default class loaded by <PhoneInput>
               * however tailwind can't parse the classes on a node module. So
               * by adding it manually here, we can pick up the styles defined
               * in tailwind.css.
               */
              'react-tel-input',
              {
                'Mui-focused': hasFocus,
                'Mui-error': invalid,
                'Mui-disabled': disabled,
              },
              className,
            )}
            country="us"
            disabled={disabled}
            disableDropdown={disabled}
            inputClass="w-full h-12 rounded-lg py-2 bg-transparent focus:shadow-transparent"
            inputProps={{
              'data-testid': dataTestid,
              disabled,
              name,
              ref,
              required: !!rules?.required,
            }}
            specialLabel={label}
            onBlur={handleBlur}
            onChange={(_value, { countryCode }, _e, formattedValue) => {
              onChange(formattedValue);
              if (countryCode !== lastChosenCountry) {
                setLastChosenCountry(countryCode);
              }
            }}
            onFocus={handleFocus}
            value={value}
            {...props}
          />
        );
      }}
    />
  );
}

ControlledPhoneInput.propTypes = {
  control: PropTypes.object.isRequired,
  dataTestid: PropTypes.string,
  defaultValue: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  errors: PropTypes.object,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  required: PropTypes.bool,
  rules: PropTypes.object,
  className: PropTypes.string,
  onCountryChange: PropTypes.func,
};

ControlledPhoneInput.defaultProps = {
  dataTestid: 'phone-input',
  errors: null,
  required: false,
  rules: null,
  className: undefined,
  onCountryChange: () => {},
};
